import classes from './register.module.scss';
import { Alert, Button, Checkbox, Form, Input, Typography } from 'antd';
import useRegister from '../../Hooks/register.hook';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Wrapper } from '../../components/shared/wrapper.component';
import { BsFillPersonLinesFill, BsPersonCircle } from 'react-icons/bs';
import { MdMail } from 'react-icons/md';
import { RiLockPasswordFill, RiLockPasswordLine } from 'react-icons/ri';
import { Constants } from '../../constants/constants';
import TermsOfServicesModal from '../../components/terms-of-services-modal/terms-of-services.component';
import useGoogleAuth from '../../Hooks/google-auth.hook';
import useFacebookAuth from '../../Hooks/facebook-auth.hook';
import { FacebookFilled } from '@ant-design/icons';
import { localizedPath } from '../../utils/localized-route';
import { useTranslation } from 'react-i18next';

const RegisterPage = () => {
  const { t } = useTranslation();
  const { register, onVerify, form, state, refreshReCaptcha } = useRegister();
  const { googleSignInHandler, loading: googleLoading } = useGoogleAuth();
  const { facebookSignInHandler, loading: facebookLoading } = useFacebookAuth();

  return (
    <Wrapper>
      <div className={classes.registerContainer}>
        <div className={classes.textContainer}>
          <h1 className={classes.title}>{t('register.title')}</h1>
          <p className={classes.text}>{t('register.description')}</p>
        </div>
        <Form
          size="large"
          layout="vertical"
          form={form}
          name="register"
          onFinish={register}
          className={classes.registerForm}
          scrollToFirstError
          disabled={state.loading}
          requiredMark={false}
        >
          <Form.Item
            className={classes.formItem}
            name="firstName"
            rules={[
              {
                required: true,
                message: t('common.required'),
                whitespace: true,
              },
            ]}
          >
            <Input
              prefix={<BsPersonCircle color="grey" />}
              placeholder={t('register.first-name')}
              size="large"
              className={classes.inputField}
            />
          </Form.Item>
          <Form.Item
            className={classes.formItem}
            name="lastName"
            rules={[
              {
                required: true,
                message: t('common.required'),
                whitespace: true,
              },
            ]}
          >
            <Input
              prefix={<BsFillPersonLinesFill color="grey" />}
              placeholder={t('register.last-name')}
              size="large"
              className={classes.inputField}
            />
          </Form.Item>
          <Form.Item
            className={classes.formItem}
            name="email"
            rules={[
              {
                type: 'email',
                message: t('common.invalid-email'),
              },
              {
                required: true,
                message: t('common.required'),
              },
            ]}
          >
            <Input
              prefix={<MdMail color="grey" />}
              placeholder={t('common.email')}
              size="large"
              className={classes.inputField}
            />
          </Form.Item>
          <Form.Item
            className={classes.formItem}
            name="password"
            rules={[
              {
                required: true,
                message: t('common.required'),
              },
              {
                pattern: Constants.PASSWORD_PATTERN,
                message: t('common.password-rules'),
              },
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<RiLockPasswordLine color="grey" />}
              placeholder={t('common.password')}
              size="large"
              className={classes.inputField}
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item
            className={classes.formItem}
            name="confirm"
            dependencies={['password']}
            hasFeedback
            rules={[
              {
                required: true,
                message: t('common.required'),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error(t('common.password-mismatch')),
                  );
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<RiLockPasswordFill color="grey" />}
              placeholder={t('register.confirm-password')}
              size="large"
              className={classes.inputField}
            />
          </Form.Item>
          <GoogleReCaptcha
            onVerify={onVerify}
            refreshReCaptcha={refreshReCaptcha}
          />
          <Form.Item
            className={classes.formItem}
            name="agreement"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                      new Error(t('validation.agreement-required')),
                    ),
              },
            ]}
          >
            <Checkbox>
              <span>{t('register.accept')}</span>
              &nbsp;
              <span>
                <TermsOfServicesModal />
              </span>
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Button
              className={classes.signUpBtn}
              loading={state.loading}
              type="primary"
              htmlType="submit"
            >
              {t('register.sign-up')}
            </Button>
            <p className={classes.loginText}>
              {t('register.have-account')}&nbsp;
              <Typography.Link href={localizedPath(`/login?dest=${state.dest}`)}>{t('register.login')}</Typography.Link>
            </p>
          </Form.Item>
          <Form.Item>
            <Typography.Text className={classes.orText}>{t('common.or')}</Typography.Text>
            <Button
              className={classes.googleBtn}
              loading={googleLoading}
              onClick={googleSignInHandler}
              icon={
                <img
                  src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                  width={18}
                  alt="Google logo"
                />
              }
            >
              {t('register.continue-google')}
            </Button>
            <Button
              className={classes.facebookBtn}
              loading={facebookLoading}
              onClick={facebookSignInHandler}
              icon={<FacebookFilled style={{ color: '#1877F2', fontSize: 20 }} />}
            >
              {t('register.continue-facebook')}
            </Button>
          </Form.Item>
        </Form>
        {state.type === 'error' && (
          <Alert
            className={classes.feedback}
            showIcon
            message={
              Array.isArray(state.result?.message)
                ? state.result?.message.map((msg, id) => {
                  return <p key={id}>{msg}</p>;
                })
                : state.result?.message
            }
            type="error"
            closable
          />
        )}
      </div>
    </Wrapper>
  );
};

export default RegisterPage;
